import { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { isMagicLinkError } from '@identity/application/errors';
import { getLinkTokenStorage, getMagicLinkConsumer } from '@identity/composition';
import { nonLinkErrorCode } from '@identity/presentation/components/base/LinkErrorScreen';
import type { LinkErrorScreenCode } from '@identity/presentation/components/base/LinkErrorScreen';
import { resolveLinkPurposeRoute } from '@identity/presentation/constants';

/** Query param the magic link carries its token in. */
const TOKEN_QUERY_PARAM = 'token';

export type UseConsumeMagicLinkResult = {
  /** The error screen to show, or `null` while the consume request is running. */
  errorCode: LinkErrorScreenCode | null;
  /** Runs the consume again with the token still in the URL. Only the retryable screens show it. */
  retry: () => void;
};

/**
 * Consumes the magic link and redirects by purpose.
 *
 * The token is deliberately left in the URL: it is single use and buys exactly the consume call, so
 * there is nothing left to protect, and keeping it means a reload or a retry still has it. The
 * redirect takes it out of the address bar on its own.
 *
 * This hook never writes the cookie and never sees the token that goes into it: the use case decides
 * by purpose whether the link session is opened, and here the purpose only chooses a route.
 *
 * It declares no flow and reads none: the consume belongs to no journey, because the landing serves
 * every purpose and only the answer says which one arrived. It is also the one call the Parrot
 * session must never be able to make, which is why it is built once with the link credential instead
 * of being reachable through whatever flow happens to be mounted.
 */
export const useConsumeMagicLink = (): UseConsumeMagicLinkResult => {
  const history = useHistory();
  const location = useLocation();
  const [errorCode, setErrorCode] = useState<LinkErrorScreenCode | null>(null);
  const [attempt, setAttempt] = useState(0);

  const retry = useCallback(() => {
    setErrorCode(null);
    setAttempt((current) => current + 1);
  }, []);

  useEffect(() => {
    const magicLinkToken = new URLSearchParams(location.search).get(TOKEN_QUERY_PARAM);

    if (!magicLinkToken) {
      setErrorCode('LINK_INVALID');

      return undefined;
    }

    let isActive = true;

    const consume = async () => {
      try {
        const { purpose } = await getMagicLinkConsumer().consumeMagicLink.execute(magicLinkToken);
        if (!isActive) return;

        const route = resolveLinkPurposeRoute(purpose);

        // Unreachable today: the repository already rejects an unknown purpose and every known one
        // has a route. It stays as a guard, and clears the session so no error screen leaves a
        // valid one behind.
        if (!route) {
          getLinkTokenStorage().clear();
          setErrorCode('LINK_INVALID');

          return;
        }

        history.replace(route);
      } catch (error) {
        if (!isActive) return;

        setErrorCode(isMagicLinkError(error) ? error.code : nonLinkErrorCode(error));
      }
    };

    consume();

    return () => {
      isActive = false;
    };
  }, [attempt]);

  return { errorCode, retry };
};
